import React, { useState } from "react";
import { Bar } from "react-chartjs-2";
import { Button } from "antd";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
} from "chart.js";

import LiquidGlass from "./LiquidGlass";
import { countsInPowerStats } from "../utils/trainerLink";

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

const color = ['rgba(128, 128, 128, 1)', 'rgba(153, 204, 255, 1)', 'rgba(0, 128, 0, 1)', 'rgba(255, 215, 0, 1)', 'rgba(255, 165, 0, 1)', 'rgba(255, 0, 0, 1)', 'rgba(128, 0, 128, 1)']

const getZoneDuration = (data, zone) => {
  const perc = [0, 0.6, 0.75, 0.9, 1.05, 1.19, 1.50, 10000].map(p => data.ftp ? p*data.ftp : p*250)

  if (data.durationSteps && data.durationSteps.length>0){
  return data.avgSteps.map((x, i) => (x >= perc[zone-1] && x < perc[zone] ? data.durationSteps[i] : 0)).reduce((a, b) => a + b, 0)
  }
  else {
    return data.avg_watt >= perc[zone-1] && data.avg_watt < perc[zone] ? data.duration : 0 // legacy
  }
}

function estimateDistance(weightCyclist, kjBurned) { //if not mentionned
  const weightBike = 8
  // Données de référence
  const referenceWeightCyclist = 74; // Poids du cycliste de référence en kg
  const referenceWeightBike = 8; // Poids du vélo de référence en kg
  const referenceKjPerKm = 348 / 17.3; // Énergie consommée par km pour 74kg + 8kg

  // Masse totale
  const referenceTotalWeight = referenceWeightCyclist + referenceWeightBike; // Masse totale de référence
  const totalWeight = weightCyclist + weightBike; // Masse totale entrée

  // Ratio d'énergie consommée par km en fonction de la masse
  const kjPerKm = referenceKjPerKm * (totalWeight / referenceTotalWeight);

  // Distance en kilomètres
  const distanceKm = kjBurned / kjPerKm;

  // Conversion en mètres
  const distanceMeters = distanceKm * 1000;
  return distanceMeters; // Distance en mètres
}

const TrainingSummary = ({ initData }) => {

  const [metric, setMetric] = useState("duration"); // "duration" or "kjoules"
  const [timeRange, setTimeRange] = useState("last_12_weeks"); // "last_12_weeks" or "last_12_months"

  //const days=["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saterday", "Sunday"]
  const data = initData.map((x) => {
    // A session ridden with no trainer has real hours and a real heart rate, but
    // no watts. Letting its zero through would drag the kJ and distance totals
    // down and pile its whole duration into zone 1.
    const hasPower = countsInPowerStats({ link: x.trainer_link });
    return {
    ...x,
    kjoules: hasPower ? x.duration * x.avg_watt / 1000 : 0,
    kcal: hasPower ? x.tot_kcal || 0 : 0,
    distance : !hasPower ? 0 : x.total_meters ? x.total_meters/1000 : estimateDistance(x.weight, x.duration * x.avg_watt / 1000)/1000,
    duration_z1: hasPower ? getZoneDuration(x, 1) : 0,
    duration_z2: hasPower ? getZoneDuration(x, 2) : 0,
    duration_z3: hasPower ? getZoneDuration(x, 3) : 0,
    duration_z4: hasPower ? getZoneDuration(x, 4) : 0,
    duration_z5: hasPower ? getZoneDuration(x, 5) : 0,
    duration_z6: hasPower ? getZoneDuration(x, 6) : 0,
    duration_z7: hasPower ? getZoneDuration(x, 7) : 0, // Example
    };
  });

  const getStartOfWeek = (date) => {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1); // Adjust for Monday start
    return new Date(d.setDate(diff));
  };
const getEndOfWeek = (startOfWeek) => {
  const endOfWeek = new Date(startOfWeek);
  endOfWeek.setDate(startOfWeek.getDate() + 6);
  return endOfWeek;
};
  const generateLabels = (groupBy, periods) => {
    const now = new Date();
    const labels = [];
    let current =
      groupBy === "week"
        ? getStartOfWeek(now)
        : new Date(now.getFullYear(), now.getMonth(), 1);
  
    if (groupBy === "day") {
      // Generate "this week" labels (Monday to Sunday)
      const startOfWeek = getStartOfWeek(now);
      for (let i = 0; i < 7; i++) {
        const date = new Date(startOfWeek);
        date.setDate(startOfWeek.getDate() + i);
        labels.push(date.toISOString().slice(0, 10)); // YYYY-MM-DD
      }
    } else {
      for (let i = 0; i < periods; i++) {
        if (groupBy === "week") {
          const startOfWeek = getStartOfWeek(current);
          const endOfWeek = getEndOfWeek(startOfWeek);
          labels.push(
            `${startOfWeek.toISOString().slice(0, 10)} - ${endOfWeek
              .toISOString()
              .slice(0, 10)}`
          );
          current.setDate(current.getDate() - 7); // Go back one week
        } else if (groupBy === "month") {
          const year = current.getFullYear();
          const month = current.getMonth() + 1; // Months are 0-based
          labels.push(`${year}-${month.toString().padStart(2, "0")}`);
          current.setMonth(current.getMonth() - 1); // Go back one month
        }
      }
    }
  
    return groupBy!=="day" ? labels.reverse() : labels;
  };

  const aggregateDataByZones = (filteredData, labels, groupBy) => {
    const baseData = labels.map((label) => ({
      label,
      z1: 0,
      z2: 0,
      z3: 0,
      z4: 0,
      z5: 0,
      z6: 0,
      z7: 0,
      duration: 0,
      distance : 0,
      kcal : 0,
      kjoules: 0,
      sessions: 0,
    }));

    filteredData.forEach((entry) => {
      const date = new Date(entry.start_time);
      let groupLabel;
  
      if (groupBy === "day") {
        groupLabel = date.toISOString().slice(0, 10);
      } else if (groupBy === "week") {
        const startOfWeek = getStartOfWeek(date);
        const endOfWeek = getEndOfWeek(startOfWeek);
        groupLabel = `${startOfWeek.toISOString().slice(0, 10)} - ${endOfWeek
          .toISOString()
          .slice(0, 10)}`;
      } else {
        groupLabel = `${date.getFullYear()}-${(date.getMonth() + 1)
          .toString()
          .padStart(2, "0")}`;
      }
  
      const found = baseData.find((item) => item.label === groupLabel);
      if (found) {
        for (let i = 1; i <= 7; i++) {
          found[`z${i}`] += entry[`duration_z${i}`] || 0;
        }
        found.duration += entry.duration || 0;
        found.kjoules += entry.kjoules || 0;
        found.kcal += entry.kcal || 0;
        found.distance += entry.distance || 0;
        found.sessions += 1;
      }
    });
  
    return baseData;
  };

  const filterData = () => {
    const now = new Date();
    let filteredData = [];
    let groupBy = "week";
    let periods = 2; // Default to last 2 weeks
    if (timeRange === "last_week") {
      groupBy = "day";
      periods = 1; // 2 weeks including the current week
      filteredData = data;
    }
    else if (timeRange === "last_12_weeks") {
      groupBy = "week";
      periods = 12; // 2 weeks including the current week
      filteredData = data;
    } else if (timeRange === "last_12_months") {
      groupBy = "month";
      periods = 12; // Last 2 months including the current month
      filteredData = data.filter(
        (d) => new Date(d.start_time) >= new Date(now.getFullYear(), now.getMonth() - 11, 1)
      );
    } else if (timeRange === "all_time") {
      groupBy = "month";
      // Compute number of months from earliest activity to now (inclusive)
      const timestamps = data.map((d) => new Date(d.start_time).getTime());
      const earliest = timestamps.length ? new Date(Math.min(...timestamps)) : now;
      periods =
        (now.getFullYear() - earliest.getFullYear()) * 12 +
        (now.getMonth() - earliest.getMonth()) + 1;
      filteredData = data;
    }
    

    const labels = generateLabels(groupBy, periods);
    return { aggregatedData: aggregateDataByZones(filteredData, labels, groupBy), labels };
  };

  const { aggregatedData, labels } = filterData();

  // Totals for the selected time window
  const totalDistanceKm = aggregatedData.reduce((sum, d) => sum + (d.distance || 0), 0);
  const totalHours = aggregatedData.reduce((sum, d) => sum + (d.duration || 0), 0) / 3600;
  const totalSessions = aggregatedData.reduce((sum, d) => sum + (d.sessions || 0), 0);

  const chartData = {
    labels,
    datasets:
      metric === "duration"
        ? Array.from({ length: 7 }, (_, i) => ({
            label: `Zone ${i + 1}`,
            data: aggregatedData.map((d) => d[`z${i + 1}`] / 3600), // Convert to hours
            backgroundColor: color[i].replace("1)", "0.5)"),
            borderColor: color[i],
            borderWidth: 1,
            stack: "durationStack", // Ensure stacking
          }))
        : metric === "distance" ? 
        [
          {
            label: "Distance (km)",
            data: aggregatedData.map((d) => d.distance),
            backgroundColor: "rgba(0,0,65,0.3)" ,
            borderColor: "rgba(0,0,65, 1)" ,
            borderWidth: 1,
            stack: "energyStack", // Ensure stacking
          },
        ] :
        metric === "kcal" ? 
        [
          {
            label: "Kcal",
            data: aggregatedData.map((d) => d.kcal),
            backgroundColor: "rgba(0,0,65,0.3)" ,
            borderColor: "rgba(0,0,65, 1)" ,
            borderWidth: 1,
            stack: "energyStack", // Ensure stacking
          },
        ]
        :[
            {
              label: "Energy (kJ)",
              data: aggregatedData.map((d) => d.kjoules),
              backgroundColor: "rgba(0,0,65,0.3)" ,
              borderColor: "rgba(0,0,65, 1)" ,
              borderWidth: 1,
              stack: "energyStack", // Ensure stacking
            },
          ],
  };

  return (
    <div style={{ fontFamily: "Arial, sans-serif" }}>
      <LiquidGlass
        className="liquid-glass"
        style={{ width: "fit-content", margin: "0 auto", maxWidth: "100%" }}
      >
        <h2 style={{ margin: 0, marginBottom: 8, color: "#0f172a", textAlign: "center" }}>
          Training Summary
        </h2>
        <i style={{ display: "flex", justifyContent: "center", gap: "10px", margin: "6px 0 12px" }}>
          <small>*Data regarding zone and distance prior 30/11/2024 are rough estimations</small>
        </i>
        <div style={{ display: "flex", justifyContent: "center", gap: 10, flexWrap: "wrap" }}>
          <Button
            className="primary-glass-btn"
            type={metric === "duration" ? "primary" : "default"}
            onClick={() => setMetric("duration")}
          >
            Duration by Zone
          </Button>
          <Button
            className="primary-glass-btn"
            type={metric === "distance" ? "primary" : "default"}
            onClick={() => setMetric("distance")}
          >
            Distance (km)
          </Button>
          <Button
            className="primary-glass-btn"
            type={metric === "kcal" ? "primary" : "default"}
            onClick={() => setMetric("kcal")}
            hidden
          >
            Kcal
          </Button>
          <Button
            className="primary-glass-btn"
            type={metric === "kjoules" ? "primary" : "default"}
            onClick={() => setMetric("kjoules")}
          >
            KJ (Kw x sec)
          </Button>
        </div>
        <div style={{ display: "flex", justifyContent: "center", gap: 10, marginTop: 10, flexWrap: "wrap" }}>
          <Button
            className="primary-glass-btn"
            type={timeRange === "last_week" ? "primary" : "default"}
            onClick={() => setTimeRange("last_week")}
          >
            This Week
          </Button>
          <Button
            className="primary-glass-btn"
            type={timeRange === "last_12_weeks" ? "primary" : "default"}
            onClick={() => setTimeRange("last_12_weeks")}
          >
            Last 3 Months
          </Button>
          <Button
            className="primary-glass-btn"
            type={timeRange === "last_12_months" ? "primary" : "default"}
            onClick={() => setTimeRange("last_12_months")}
          >
            Last Year
          </Button>
          <Button
            className="primary-glass-btn"
            type={timeRange === "all_time" ? "primary" : "default"}
            onClick={() => setTimeRange("all_time")}
          >
            All Time
          </Button>
        </div>
      </LiquidGlass>

      <LiquidGlass
        className="liquid-glass"
        style={{ marginTop: 12, marginLeft: "auto", marginRight: "auto", maxWidth: 1290, width: "100%" }}
      >
        <Bar
          data={chartData}
          options={{
            responsive: true,
            maintainAspectRatio: true,
            aspectRatio: 2.0,
            plugins: {
              tooltip: {
                callbacks: {
                  label: (context) =>
                    metric === "duration"
                      ? `${context.dataset.label}: ${context.raw.toFixed(2)} h`
                      : metric === "distance"
                      ? `${context.dataset.label}: ${context.raw.toFixed(2)} km`
                      : metric === "kcal"
                      ? `${context.dataset.label}: ${context.raw.toFixed(2)} kcal`
                      : `${context.dataset.label}: ${context.raw.toFixed(2)} kJ`,
                },
              },
            },
            scales: {
              y: {
                beginAtZero: true,
                stacked: true,
                title: {
                  display: true,
                  text:
                    metric === "duration"
                      ? "Duration (hours)"
                      : metric === "distance"
                      ? "Distance (km)"
                      : metric === "kcal"
                      ? "Kcal"
                      : "Energy (kJ)",
                },
              },
              x: {
                stacked: true,
                title: {
                  display: true,
                  text: (timeRange === "last_12_months" || timeRange === "all_time") ? "Months" : timeRange === "last_12_weeks" ? "Weeks" : "Days",
                },
              },
            },
          }}
        />
      </LiquidGlass>
      
      <LiquidGlass
        className="liquid-glass"
        style={{ marginTop: 12, width: "fit-content", marginLeft: "auto", marginRight: "auto", maxWidth: "100%" }}
      >
        <div style={{ display: "flex", justifyContent: "center", gap: 24, flexWrap: "wrap" }}>
          <div><b>Total km:</b> {totalDistanceKm.toFixed(1)} km</div>
          <div><b>Total hours:</b> {totalHours.toFixed(1)} h</div>
          <div><b>Total sessions:</b> {totalSessions}</div>
        </div>
      </LiquidGlass>
    </div>
  );
};

export default TrainingSummary;